import { useState } from 'react';
import { formatDateNumeric } from "../../utils/formatters";
import {
  Stack,
  Card,
  Table,
  Badge,
  Group,
  Text,
  Select,
  ActionIcon,
  Tooltip,
  Alert,
  Loader,
} from '@mantine/core';
import { IconCheck, IconX, IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useFacilityContext } from '../../hooks/useFacilityContext';
import { useFacilityAuditRequests } from '../../hooks/useAuditRequests';
import { AuditRequestApprovalModal } from '../../components/audit/AuditRequestApprovalModal';
import { useFacilities } from '../../hooks/useFacilities';
import type { AuditRequestStatus, AuditAccessRequest } from '../../types/audit';

/**
 * Pending Audit Requests Tab
 * Shows all audit access requests for the manager's facilities
 */
export function PendingAuditRequestsTab() {
  const { currentFacilityId, facilityIds } = useFacilityContext();
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>(
    currentFacilityId || facilityIds[0] || ''
  );
  const [statusFilter, setStatusFilter] = useState<AuditRequestStatus | ''>('pending');
  const [selectedRequest, setSelectedRequest] = useState<AuditAccessRequest | null>(null);
  const [approvalModalOpened, { open: openApprovalModal, close: closeApprovalModal }] =
    useDisclosure();

  // Fetch facilities to get names
  const { data: facilities } = useFacilities();

  const { data: requests, isLoading, isError } = useFacilityAuditRequests(
    selectedFacilityId,
    statusFilter || undefined
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'green';
      case 'denied':
        return 'red';
      case 'expired':
        return 'gray';
      case 'pending':
      default:
        return 'yellow';
    }
  };

  const handleApprove = (request: AuditAccessRequest) => {
    setSelectedRequest(request);
    openApprovalModal();
  };

  const handleDeny = (request: AuditAccessRequest) => {
    setSelectedRequest(request);
    openApprovalModal();
  };

  const pendingCount = requests?.filter((r) => r.status === 'pending').length || 0;

  // Create facility options with names
  const facilityOptions = facilities
    ?.filter((f) => facilityIds.includes(f.id))
    .map((f) => ({ value: f.id, label: f.name })) || [];

  return (
    <Stack gap="md">
      {/* Alert for pending requests */}
      {pendingCount > 0 && (
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
          You have {pendingCount} pending audit request{pendingCount !== 1 ? 's' : ''} awaiting
          review
        </Alert>
      )}

      {/* Filters */}
      <Card withBorder>
        <Group>
          {facilityIds.length > 1 && (
            <Select
              label="Facility"
              placeholder="Select facility"
              data={facilityOptions}
              value={selectedFacilityId}
              onChange={(value) => setSelectedFacilityId(value || facilityIds[0])}
              style={{ flex: 1, maxWidth: 300 }}
            />
          )}
          <Select
            label="Status"
            placeholder="All statuses"
            data={[
              { value: '', label: 'All' },
              { value: 'pending', label: 'Pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'denied', label: 'Denied' },
              { value: 'expired', label: 'Expired' },
            ]}
            value={statusFilter}
            onChange={(value) => setStatusFilter((value as AuditRequestStatus) || '')}
            clearable
            style={{ flex: 1, maxWidth: 300 }}
          />
        </Group>
      </Card>

      {/* Requests Table */}
      <Card withBorder>
        {isLoading ? (
          <Stack align="center" py="xl">
            <Loader size="md" />
            <Text c="dimmed">Loading audit requests...</Text>
          </Stack>
        ) : isError ? (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            Failed to load audit requests. Please try refreshing the page.
          </Alert>
        ) : !requests || requests.length === 0 ? (
          <Stack align="center" py="xl" gap="xs">
            <IconInfoCircle size={48} stroke={1.5} color="gray" />
            <Text c="dimmed">
              {statusFilter
                ? `No ${statusFilter} audit requests found.`
                : 'No audit requests for this facility.'}
            </Text>
          </Stack>
        ) : (
          <Table.ScrollContainer minWidth={1000}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ textAlign: 'left' }}>Status</Table.Th>
                  <Table.Th style={{ textAlign: 'left' }}>Auditor</Table.Th>
                  <Table.Th style={{ textAlign: 'left' }}>Organization</Table.Th>
                  <Table.Th style={{ textAlign: 'left' }}>Audit Type</Table.Th>
                  <Table.Th style={{ textAlign: 'left' }}>Requested Period</Table.Th>
                  <Table.Th style={{ textAlign: 'left' }}>Purpose</Table.Th>
                  <Table.Th style={{ textAlign: 'left' }}>Created</Table.Th>
                  <Table.Th style={{ textAlign: 'left', width: 120 }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {requests.map((request) => (
                  <Table.Tr key={request.id}>
                    <Table.Td>
                      <Badge color={getStatusColor(request.status)}>{request.status}</Badge>
                    </Table.Td>
                    <Table.Td>
                      {/* F-091 — this read "N/A", which on the surface where a
                          customer authorises an outside party to read
                          residents' records means "no auditor". The identity
                          was always on the row as a Cognito sub; the query
                          just never resolved it. An unresolved lookup now says
                          so explicitly instead of borrowing the word for
                          "none". */}
                      {request.auditor?.email ? (
                        <Stack gap={0}>
                          {request.auditor.name && (
                            <Text size="sm" fw={500}>
                              {request.auditor.name}
                            </Text>
                          )}
                          <Text size="sm" c={request.auditor.name ? 'dimmed' : undefined}>
                            {request.auditor.email}
                          </Text>
                        </Stack>
                      ) : (
                        <Text size="sm" c="dimmed" fs="italic">
                          Auditor identity unavailable
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{request.auditor_organization}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" tt="capitalize">
                        {request.audit_type.replace('_', ' ')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {formatDateNumeric(request.requested_start_date)}
                        {' - '}
                        {formatDateNumeric(request.requested_end_date)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={2} maw={200}>
                        {request.audit_purpose}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {formatDateNumeric(request.created_at)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {request.status === 'pending' ? (
                        <Group gap="xs" wrap="nowrap">
                          <Tooltip label="Approve request" withArrow>
                            <ActionIcon
                              color="green"
                              variant="light"
                              onClick={() => handleApprove(request)}
                            >
                              <IconCheck size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Deny request" withArrow>
                            <ActionIcon
                              color="red"
                              variant="light"
                              onClick={() => handleDeny(request)}
                            >
                              <IconX size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      ) : (
                        <Text size="sm" c="dimmed">
                          {request.status === 'approved' && request.reviewed_at
                            ? `Approved ${formatDateNumeric(request.reviewed_at)}`
                            : request.status === 'denied' && request.reviewed_at
                              ? `Denied ${formatDateNumeric(request.reviewed_at)}`
                              : '—'}
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      {/* Summary */}
      {requests && requests.length > 0 && (
        <Card withBorder>
          <Group>
            <Text size="sm" c="dimmed">
              Total: {requests.length}
            </Text>
            <Text size="sm" c="dimmed">
              •
            </Text>
            <Text size="sm" c="dimmed">
              Pending: {requests.filter((r) => r.status === 'pending').length}
            </Text>
            <Text size="sm" c="dimmed">
              •
            </Text>
            <Text size="sm" c="dimmed">
              Approved: {requests.filter((r) => r.status === 'approved').length}
            </Text>
          </Group>
        </Card>
      )}

      {/* Approval/Denial Modal */}
      <AuditRequestApprovalModal
        request={selectedRequest}
        opened={approvalModalOpened}
        onClose={closeApprovalModal}
      />
    </Stack>
  );
}
